import { useState, useCallback } from 'react'
import { useAuthenticatedUser } from '../../../hooks/useAuthenticatedUser'
import {
	useAssignDemandRequestMutation,
	useCompleteDemandRequestMutation,
	useReopenDemandRequestMutation,
	useCancelDemandRequestMutation
} from '../../../graphql'
import { useShowError, useShowSuccess } from '../../../hooks/useToast'


// Query names for cache refetching
const REFETCH_QUERIES = [ 'DemandRequests', 'DemandRequest' ]

export type UseDemandRequestActionsReturn = {
	// Actions
	assignToMe: ( requestId: string ) => Promise<void>,
	unassignFromMe: ( requestId: string ) => Promise<void>,
	markAsCompleted: ( requestId: string ) => Promise<void>,
	reopen: ( requestId: string ) => Promise<void>,
	cancel: ( requestId: string ) => Promise<void>,

	// Loading states
	isAssigningToMe: boolean,
	isUnassigningFromMe: boolean,
	isMarkingAsCompleted: boolean,
	isReopening: boolean,
	isCancelling: boolean,

	// Error states
	assignError: Error | null,
	unassignError: Error | null,
	completeError: Error | null,
	reopenError: Error | null,
	cancelError: Error | null,
}

export function useDemandRequestActions(): UseDemandRequestActionsReturn {
	const authenticatedUser = useAuthenticatedUser()
	const [ assignDemandRequest ] = useAssignDemandRequestMutation()
	const [ completeDemandRequest ] = useCompleteDemandRequestMutation()
	const [ reopenDemandRequest ] = useReopenDemandRequestMutation()
	const [ cancelDemandRequest ] = useCancelDemandRequestMutation()
	const showSuccess = useShowSuccess()
	const showError = useShowError()
	const [ isAssigningToMe, setIsAssigningToMe ] = useState( false )
	const [ isUnassigningFromMe, setIsUnassigningFromMe ] = useState( false )
	const [ isMarkingAsCompleted, setIsMarkingAsCompleted ] = useState( false )
	const [ isReopening, setIsReopening ] = useState( false )
	const [ isCancelling, setIsCancelling ] = useState( false )
	const [ assignError, setAssignError ] = useState<Error | null>( null )
	const [ unassignError, setUnassignError ] = useState<Error | null>( null )
	const [ completeError, setCompleteError ] = useState<Error | null>( null )
	const [ reopenError, setReopenError ] = useState<Error | null>( null )
	const [ cancelError, setCancelError ] = useState<Error | null>( null )

	const assignToMe = useCallback(
		async( requestId: string ) => {
			if ( !authenticatedUser.id ) {
				const error = new Error( 'User not authenticated' )
				setAssignError( error )
				showError( 'assign-request', 'You must be logged in to assign requests' )
				return
			}

			setIsAssigningToMe( true )
			setAssignError( null )

			try {
				const result = await assignDemandRequest( {
					variables: {
						input: {
							demandRequestId: requestId,
							assignedToUserId: authenticatedUser.id,
						},
					},
					refetchQueries: REFETCH_QUERIES,
					awaitRefetchQueries: true,
				} )

				if ( result.data?.assignDemandRequest?.success ) {
					showSuccess( 'assign-request', 'Request assigned to you successfully' )
				} else {
					throw new Error(
						result.data?.assignDemandRequest?.message ?? 'Failed to assign request'
					)
				}
			} catch ( error ) {
				const err = error instanceof Error ? error : new Error( 'Failed to assign request' )
				setAssignError( err )
				showError( 'assign-request', err.message )
			} finally {
				setIsAssigningToMe( false )
			}
		},
		[ authenticatedUser.id, assignDemandRequest, showSuccess, showError ]
	)

	const unassignFromMe = useCallback(
		async( requestId: string ) => {
			setIsUnassigningFromMe( true )
			setUnassignError( null )

			try {
				const result = await assignDemandRequest( {
					variables: {
						input: {
							demandRequestId: requestId,
							assignedToUserId: null,
						},
					},
					refetchQueries: REFETCH_QUERIES,
					awaitRefetchQueries: true,
				} )

				if ( result.data?.assignDemandRequest?.success ) {
					showSuccess( 'unassign-request', 'Request unassigned successfully' )
				} else {
					throw new Error(
						result.data?.assignDemandRequest?.message ?? 'Failed to unassign request'
					)
				}
			} catch ( error ) {
				const err = error instanceof Error ? error : new Error( 'Failed to unassign request' )
				setUnassignError( err )
				showError( 'unassign-request', err.message )
			} finally {
				setIsUnassigningFromMe( false )
			}
		},
		[ assignDemandRequest, showSuccess, showError ]
	)

	const markAsCompleted = useCallback(
		async( requestId: string ) => {
			setIsMarkingAsCompleted( true )
			setCompleteError( null )

			try {
				const result = await completeDemandRequest( {
					variables: {
						input: {
							demandRequestId: requestId,
						},
					},
					refetchQueries: REFETCH_QUERIES,
					awaitRefetchQueries: true,
				} )

				const responseMessage = result.data?.completeDemandRequest?.message ?? ''
				const isAlreadyCompleted = responseMessage.toLowerCase().includes( 'already completed' )

				if ( result.data?.completeDemandRequest?.success || isAlreadyCompleted ) {
					showSuccess( 'complete-request', 'Request marked as completed' )
				} else {
					throw new Error( result.data?.completeDemandRequest?.message ?? 'Failed to complete request' )
				}
			} catch ( error ) {
				const err = error instanceof Error ? error : new Error( 'Failed to complete request' )
				setCompleteError( err )
				showError( 'complete-request', err.message )
			} finally {
				setIsMarkingAsCompleted( false )
			}
		},
		[ completeDemandRequest, showSuccess, showError ]
	)

	const reopen = useCallback(
		async( requestId: string ) => {
			setIsReopening( true )
			setReopenError( null )

			try {
				const result = await reopenDemandRequest( {
					variables: {
						input: {
							id: requestId,
						},
					},
					refetchQueries: REFETCH_QUERIES,
					awaitRefetchQueries: true,
				} )

				if ( result.data?.reopenDemandRequest?.success ) {
					showSuccess( 'reopen-request', 'Request reopened successfully' )
				} else {
					throw new Error(
						result.data?.reopenDemandRequest?.message ?? 'Failed to reopen request'
					)
				}
			} catch ( error ) {
				const err = error instanceof Error ? error : new Error( 'Failed to reopen request' )
				setReopenError( err )
				showError( 'reopen-request', err.message )
			} finally {
				setIsReopening( false )
			}
		},
		[ reopenDemandRequest, showSuccess, showError ]
	)

	const cancel = useCallback(
		async( requestId: string ) => {
			setIsCancelling( true )
			setCancelError( null )

			try {
				const result = await cancelDemandRequest( {
					variables: {
						input: {
							demandRequestId: requestId,
						},
					},
					refetchQueries: REFETCH_QUERIES,
					awaitRefetchQueries: true,
				} )

				if ( result.data?.cancelDemandRequest?.success ) {
					showSuccess( 'cancel-request', 'Request cancelled successfully' )
				} else {
					throw new Error(
						result.data?.cancelDemandRequest?.message ?? 'Failed to cancel request'
					)
				}
			} catch ( error ) {
				const err = error instanceof Error ? error : new Error( 'Failed to cancel request' )
				setCancelError( err )
				showError( 'cancel-request', err.message )
			} finally {
				setIsCancelling( false )
			}
		},
		[ cancelDemandRequest, showSuccess, showError ]
	)

	return {
		assignToMe,
		unassignFromMe,
		markAsCompleted,
		reopen,
		cancel,
		isAssigningToMe,
		isUnassigningFromMe,
		isMarkingAsCompleted,
		isReopening,
		isCancelling,
		assignError,
		unassignError,
		completeError,
		reopenError,
		cancelError,
	}
}
